/**
 * Lifts top-level `blockId` / `layoutId` on a payload into a `_analytics`
 * namespace, merging with any existing `_analytics` keys. This is the single
 * convention for block attribution on action payloads, consistent with the
 * existing `_analytics` namespace used by experiment tracking.
 *
 * RN strips `_analytics` as a unit from every third-party adapter payload, so
 * anything under this namespace is guaranteed to stay within Clickstream.
 */
export const toAnalyticsPayload = <T extends Record<string, any>>(
  payload: T & {
    blockId?: string
    layoutId?: string
    _analytics?: Record<string, any>
  }
): Omit<T, "blockId" | "layoutId"> & { _analytics?: Record<string, any> } => {
  // Guard against null/undefined/primitive payloads — destructuring those would
  // throw synchronously during render and trip the client error boundary.
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return payload as any
  }

  const {
    blockId,
    layoutId,
    _analytics: existing,
    ...rest
  } = payload as Record<string, any>

  const merged: Record<string, any> = { ...(existing ?? {}) }
  if (blockId) merged.blockId = blockId
  if (layoutId) merged.layoutId = layoutId

  if (Object.keys(merged).length === 0) {
    return rest as any
  }
  return { ...rest, _analytics: merged } as any
}

// SDK named functions with Yup schemas strip unknown fields like _analytics.
// These specific functions need to be routed through the generic dispatch
// to preserve block attribution.
const FUNCTION_TO_ACTION: Record<string, string> = {
  openScreen: "screen/open",
  openProduct: "product/open",
  openCollection: "collection/open",
  addToCart: "cart/add",
}

/**
 * Wraps every own function property on an actions object so that when a block
 * invokes it with a plain-object payload (e.g. `actions.openProduct({ productId })`),
 * `blockId` and `layoutId` get merged into `payload._analytics` before it
 * crosses the bridge. Non-object first arguments (strings, numbers, undefined)
 * are left untouched so methods like `actions.trackAnalyticEvent(name, payload)`
 * or `actions.addItemToWishlist(productId, variantId, wishlistId)` still work.
 *
 * For named SDK functions that have Yup schema validation (which strips unknown
 * fields like `_analytics`), we route through the generic `action()` dispatch
 * instead, which passes the payload through without validation.
 *
 * Returns a plain object with stable wrapped function references — consumers
 * using `useEffect`/`useCallback` deps on individual methods (e.g.
 * `[actions.openScreen]`) won't see identity churn between renders. Callers
 * should memoize the full result on `(originalActions, blockId, layoutId)`.
 */
export const enrichActionsWithBlockContext = <T extends Record<string, any>>(
  originalActions: T,
  blockId: string,
  layoutId: string | undefined
): T => {
  if (!originalActions || typeof originalActions !== "object") {
    return originalActions
  }

  const genericDispatch = (originalActions as any).action
  const wrapped: Record<string, any> = {}
  for (const key of Object.keys(originalActions)) {
    const value = (originalActions as any)[key]
    if (typeof value !== "function") {
      wrapped[key] = value
      continue
    }
    const actionName = FUNCTION_TO_ACTION[key]
    wrapped[key] = (...args: any[]) => {
      const [first, ...rest] = args
      if (first && typeof first === "object" && !Array.isArray(first)) {
        const enrichedPayload = toAnalyticsPayload({
          blockId,
          layoutId,
          ...first,
        })
        if (actionName && genericDispatch) {
          return genericDispatch(actionName, enrichedPayload, ...rest)
        }
        return value.apply(originalActions, [enrichedPayload, ...rest])
      }
      return value.apply(originalActions, args)
    }
  }
  return wrapped as T
}
